import React, { useEffect } from "react";
import Traec from "traec";
import { BSBtnDropdown, BSCard, BSBtn } from "traec-react/utils/bootstrap";
import { connect } from "react-redux";
import { getProjectProps } from "AppSrc/project/utils";
import Octicon from "react-octicon";
import DataTable from "react-data-table-component";

function SupplierRequestsHeader({ requestCount }) {
  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="h3 mb-0 text-primary">Supplier Requests</h1>
            </div>
            <p className="text-muted mb-2">Manage supplier access requests</p>
            <p className="text-muted small mb-0">
              <strong>Pending requests:</strong> {requestCount}
            </p>
          </div>
          <div className="text-end">
            <span className="badge bg-warning text-dark">Pending Review</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SupplierDescription() {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">Supplier Access Management</h2>
      <p className="text-muted">
        Review and manage supplier requests to join this project. You can approve or reject requests, assign appropriate
        permissions, and control supplier access levels.
      </p>
      <div className="alert alert-info">
        <div className="d-flex">
          <Octicon name="organization" className="me-2 mt-1" />
          <div>
            <strong>Supplier Access:</strong> When you approve a supplier request, they will gain access to relevant
            project information based on the permissions you assign. Rejected requests will be denied access.
          </div>
        </div>
      </div>
    </div>
  );
}

export const PendingSupplierRequests = (props) => {
  if (!props.project.getInPath("meta_json.allowSuppliers")) {
    return null;
  }

  new Traec.Fetch("project_supplier", "list", { projectId: props.projectId }).dispatch();
  new Traec.Fetch("project_authgroup", "list", { projectId: props.projectId }).dispatch();

  // Get pending requests
  const pendingRequests = (props.supplierRequests || Traec.Im.List()).filter(
    (request) => request.get("status") === "outstanding"
  );

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <SupplierRequestsHeader requestCount={pendingRequests.size} />

          <div className="card shadow-sm">
            <div className="card-body">
              <SupplierDescription />

              <RequestTable
                requests={pendingRequests}
                authGroups={props.authGroups}
                refId={props.crefId}
                commitId={props.commitId}
                trackerId={props.trackerId}
                treeId={props.rootTreeId}
                projects={props.projects}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  let { project } = ownProps;
  let projectId = project.get("uid");
  let projects = state.getInPath(`entities.projects.byId`);
  let supplierRequests = projectId ? state.getInPath(`entities.projectObjects.byId.${projectId}.requests`) : null;
  let authGroups = projectId ? state.getInPath(`entities.projectObjects.byId.${projectId}.authGroups`) : null;
  let { trackerId, cref, crefId } = getProjectProps(state, projectId, null);
  let commit = cref ? cref.get("latest_commit") : null;
  let commitId = commit ? commit.get("uid") : null;
  let rootTreeId = commit ? commit.getIn(["tree_root", "uid"]) : null;

  return { projectId, project, projects, supplierRequests, authGroups, trackerId, cref, crefId, commitId, rootTreeId };
};

export default connect(mapStateToProps)(PendingSupplierRequests);

const RequestTable = (props) => {
  if (!props.requests || props.requests.size === 0) {
    return (
      <div className="text-center py-5">
        <Octicon name="organization" className="text-muted mb-3" style={{ fontSize: "48px" }} />
        <h5 className="text-muted">No pending supplier requests</h5>
        <p className="text-muted">All supplier requests have been processed or no requests have been submitted yet.</p>
      </div>
    );
  }

  const columns = [
    {
      name: "Supplier",
      selector: (row) =>
        `${row.getInPath("supplier_company.name")} - ${row.getInPath("supplier_discipline.project.name")}`,
      grow: 2,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-column">
          <span className="fw-medium">{row.getInPath("supplier_company.name")}</span>
          <small className="text-muted">{row.getInPath("supplier_discipline.project.name")}</small>
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <RequestRowActions
          request={row}
          authGroups={props.authGroups}
          trackerId={props.trackerId}
          refId={props.refId}
          commitId={props.commitId}
          treeId={props.treeId}
          projects={props.projects}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "200px",
    },
  ];

  return (
    <div className="table-responsive">
      <DataTable
        columns={columns}
        data={props.requests.toArray()}
        highlightOnHover={true}
        customStyles={{
          table: {
            style: {
              borderRadius: "8px",
            },
          },
          headRow: {
            style: {
              backgroundColor: "#f8f9fa",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            },
          },
          rows: {
            style: {
              "&:hover": {
                backgroundColor: "#f8f9fa",
              },
            },
          },
        }}
      />
    </div>
  );
};

const RequestRows = (props) => {
  if (!props.requests) {
    return <div>No pending requests</div>;
  }

  let pendingRequests = props.requests.filter((request) => {
    if (request.get("status") === "outstanding") {
      return request;
    }
  });

  if (!pendingRequests.size) {
    return <div>No pending requests</div>;
  }

  return pendingRequests
    .valueSeq()
    .map((request, index) => (
      <RequestRow
        key={index}
        index={index}
        request={request}
        authGroups={props.authGroups}
        trackerId={props.trackerId}
        refId={props.refId}
        commitId={props.commitId}
        treeId={props.treeId}
        projects={props.projects}
      />
    ));
};

const RequestRowActions = (props) => {
  // Get the default authority group to use
  let defaultAuthGroup = (props.authGroups || Traec.Im.Map())
    .toList()
    .filter((i) => i.get("name").startsWith("Report"))
    .first();
  defaultAuthGroup = defaultAuthGroup ? defaultAuthGroup.toJS() : { name: "Select Permissions", uid: null };

  const [state, setState] = React.useState({
    supplierName: props.request.getInPath("supplier_company.name"),
    authGroup: defaultAuthGroup,
    showAcceptForm: false,
    refName: `${props.request.getInPath("supplier_discipline.project.name")} - Reporting Package`,
  });

  const handleAction = (e, action) => {
    if (action === "accepted") {
      handleAcceptance(action);
    } else {
      handleRejecting(action);
    }
  };

  const toggleAcceptForm = () => {
    setState({ ...state, showAcceptForm: !state.showAcceptForm });
  };

  const handleReject = () => {
    if (window.confirm("Are you sure you want to reject this supplier request?")) {
      handleAction(null, "rejected");
    }
  };

  if (state.showAcceptForm) {
    return (
      <div className="d-flex flex-column gap-2">
        <div className="input-group input-group-sm">
          <span className="input-group-text">Name</span>
          <input
            className="form-control"
            value={state.supplierName || ""}
            onChange={(e) => setState({ ...state, supplierName: e.target.value })}
            placeholder="Supplier name"
          />
        </div>
        <div className="input-group input-group-sm">
          <span className="input-group-text">Package</span>
          <input
            className="form-control"
            value={state.refName || ""}
            onChange={(e) => setState({ ...state, refName: e.target.value })}
            placeholder="Package name"
          />
        </div>
        <select
          className="form-select form-select-sm"
          value={state.authGroup.uid || ""}
          onChange={(e) => {
            const selectedAuth = props.authGroups.find((auth) => auth.get("uid") === e.target.value);
            setState({
              ...state,
              authGroup: selectedAuth
                ? { uid: selectedAuth.get("uid"), name: selectedAuth.get("name") }
                : defaultAuthGroup,
            });
          }}
        >
          <option value="">Select permissions...</option>
          {(props.authGroups || Traec.Im.List()).map((auth, k) => (
            <option key={k} value={auth.get("uid")}>
              {auth.get("name")}
            </option>
          ))}
        </select>
        <div className="d-flex gap-1">
          <button className="btn btn-success btn-sm flex-fill" onClick={(e) => handleAction(e, "accepted")}>
            <Octicon name="check" className="me-1" />
            Accept
          </button>
          <button className="btn btn-secondary btn-sm" onClick={toggleAcceptForm}>
            <Octicon name="x" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex gap-1">
      <button className="btn btn-success btn-sm" onClick={toggleAcceptForm} title="Accept supplier request">
        <Octicon name="check" />
      </button>
      <button className="btn btn-danger btn-sm" onClick={handleReject} title="Reject supplier request">
        <Octicon name="x" />
      </button>
    </div>
  );

  function handleAcceptance(action) {
    let fetch = new Traec.Fetch("tracker_ref_tree_branch", "post", {
      trackerId: props.trackerId,
      refId: props.refId,
      commitId: props.commitId,
      treeId: props.treeId,
      skip_categories: true,
    });
    fetch.updateFetchParams({
      body: {
        name: "reporting package tree",
        ref_name: state.refName || `${props.request.getInPath("supplier_discipline.project.name")} - Reporting Package`,
      },
      nextHandlers: [
        (data, post, orgPost) => {
          let nextFetch = new Traec.Fetch("project_supplier", "put", {
            projectId: props.request.getIn(["client_project", "uid"]),
            requestId: props.request.get("uid"),
          });
          nextFetch.updateFetchParams({
            body: {
              status: action,
              new_name:
                state.supplierName || `${props.request.getInPath("supplier_discipline.project.name")} - Supplier`,
              auth_group: state.authGroup.uid,
              client_ref: data.target.ref.uid,
            },
          });
          return nextFetch.params;
        },
      ],
    });
    fetch.dispatch();
  }

  function handleRejecting(action) {
    let fetch = new Traec.Fetch("project_supplier", "put", {
      projectId: props.request.getIn(["client_project", "uid"]),
      requestId: props.request.get("uid"),
    });
    fetch.updateFetchParams({
      body: {
        status: action,
      },
    });
    fetch.dispatch();
  }
};

const RequestRow = (props) => {
  // Get the default authority group to use
  let defaultAuthGroup = (props.authGroups || Traec.Im.Map())
    .toList()
    .filter((i) => i.get("name").startsWith("Report"))
    .first();
  defaultAuthGroup = defaultAuthGroup ? defaultAuthGroup.toJS() : { name: "Select Authority Group", uid: null };

  //
  const [state, setState] = React.useState({
    supplierName: null,
    authGroup: defaultAuthGroup,
    showAccept: false,
    refName: null,
  });

  const handleAction = (e, action) => {
    if (action === "accepted") {
      handleAcceptance(action);
    } else {
      handleRejecting(action);
    }
  };

  const handleAcceptance = (action) => {
    let fetch = new Traec.Fetch("tracker_ref_tree_branch", "post", {
      trackerId: props.trackerId,
      refId: props.refId,
      commitId: props.commitId,
      treeId: props.treeId,
      skip_categories: true,
    });
    fetch.updateFetchParams({
      body: {
        name: "reporting package tree",
        ref_name: state.refName
          ? state.refName
          : `${props.request.getIn(["supplier_discipline", "project", "name"])} - Reporting Package`,
      },
      nextHandlers: [
        (data, post, orgPost) => {
          let nextFetch = new Traec.Fetch("project_supplier", "put", {
            projectId: props.request.getIn(["client_project", "uid"]),
            requestId: props.request.get("uid"),
          });
          nextFetch.updateFetchParams({
            body: {
              status: action,
              new_name: state.supplierName
                ? state.supplierName
                : `${props.request.getIn(["supplier_discipline", "project", "name"])} - Supplier`,
              auth_group: state.authGroup.uid,
              client_ref: data.target.ref.uid,
            },
          });
          return nextFetch.params;
        },
      ],
    });
    fetch.dispatch();
  };

  const handleRejecting = (action) => {
    let fetch = new Traec.Fetch("project_supplier", "put", {
      projectId: props.request.getIn(["client_project", "uid"]),
      requestId: props.request.get("uid"),
    });
    fetch.updateFetchParams({
      body: {
        status: action,
      },
    });
    fetch.dispatch();
  };

  const toggleAcceptForm = (e) => {
    setState({ ...state, showAccept: !state.showAccept });
  };

  const updateAuth = (e, auth) => {
    setState({ ...state, authGroup: { uid: auth.get("uid"), name: auth.get("name") } });
  };

  const updateSupplierName = (e) => {
    setState({ ...state, supplierName: e.target.value });
  };

  const updateRefName = (e) => {
    setState({ ...state, refName: e.target.value });
  };

  let supplierProjectName = props.request.getInPath("supplier_discipline.project.name");
  let supplierCompanyName = props.request.getInPath("supplier_company.name");

  return (
    <div className="row" style={{ backgroundColor: (props.index + 1) % 2 ? "#ddd" : "" }}>
      <div className="col-3 mt-1 mb-1 pt-1 pb-1">{`${supplierCompanyName} - ${supplierProjectName}`}</div>
      <div className="col-8">
        <AcceptForm
          show={state.showAccept}
          updateSupplierName={updateSupplierName}
          updateRefName={updateRefName}
          refName={`${props.request.getInPath("supplier_discipline.project.name")}`}
          supplierName={`${props.request.getInPath("supplier_company.name")}`}
          updateAuth={updateAuth}
          authGroups={props.authGroups}
          selectedAuth={state.authGroup}
        />
      </div>
      <div className="col-1 mt-1 mb-1 pt-1 pb-1">
        {state.showAccept ? (
          <BSBtn text="Accept" onClick={(e) => handleAction(e, "accepted")} />
        ) : (
          <BSBtnDropdown
            links={[
              { name: "Accept", onClick: (e) => toggleAcceptForm(e) },
              { name: "Reject", onClick: (e) => handleAction(e, "rejected") },
            ]}
          />
        )}
      </div>
    </div>
  );
};

const AcceptForm = (props) => {
  if (!props.show) {
    return null;
  }

  let links = props.authGroups.valueSeq().map((auth) => {
    return { name: auth.get("name"), onClick: (e) => props.updateAuth(e, auth) };
  });

  return (
    <div className="row m-1 p-1">
      <div className="input-group input-group-sm col-4">
        <input className="form-control" defaultValue={props.refName} type="text" onChange={props.updateRefName} />
      </div>
      <div className="input-group input-group-sm col-4">
        <input
          className="form-control"
          defaultValue={props.supplierName || "Give supplier a name"}
          type="text"
          onChange={props.updateSupplierName}
        />
      </div>

      <div className="col-4">
        <BSBtnDropdown header={props.selectedAuth.name} links={links.toJS()} />
      </div>
    </div>
  );
};
